import { GristDoc } from "app/client/components/GristDoc";
import { getIndexedDBStore } from "app/client/lib/indexedDBStore";
import { makeT } from "app/client/lib/localization";
import * as log from "app/client/lib/log";
import { getStorage } from "app/client/lib/storage";
import { ChatHistory, ChatMessage } from "app/client/models/ChatHistory";
import { reportError } from "app/client/models/errors";
import { AssistanceState, DeveloperPromptVersion } from "app/common/Assistance";
import { parseUrlId } from "app/common/gristUrls";
import { safeJsonParse } from "app/common/gutil";

import { Computed, Disposable, Observable } from "grainjs";
import { v4 as uuidv4 } from "uuid";

// eslint-disable-next-line local/makeT-filename
const t = makeT("Assistant");

const MAX_SESSION_TITLE_LENGTH = 60;

export const Deps = { getIndexedDBStore, getStorage, reportError };

/**
 * The part of a session that's loaded upfront, for listing it in the history menu.
 */
export interface AssistantSessionSummary {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * The rest of a session, loaded when the session is opened.
 */
export interface AssistantSessionContents {
  messages: ChatMessage[];
  state?: AssistanceState;
  developerPromptVersion?: DeveloperPromptVersion;
}

export type AssistantSession = AssistantSessionSummary & AssistantSessionContents;

/**
 * Stores a document's assistant sessions in IndexedDB.
 *
 * Each session is stored as two items: a summary, and its contents. Summaries of all sessions are
 * loaded upfront, while contents are only loaded when a session is opened.
 */
export class AssistantSessionStore extends Disposable {
  /** Summaries of stored sessions, most recently updated first. */
  public readonly sessions = Observable.create<AssistantSessionSummary[]>(this, []);
  public readonly isLoaded = Observable.create(this, false);
  public readonly isPersistent = Observable.create(this, true);

  /** Id of the active session. A new session isn't stored (or in `sessions`) until it has messages. */
  public readonly activeSessionId = Observable.create(this, uuidv4());

  /** Summary of the active session, or null if it's new. */
  public readonly activeSession: Computed<AssistantSessionSummary | null>;

  /** Whether the active session's contents are still loading. */
  public readonly isActiveSessionLoading: Computed<boolean>;

  private _appModel = this._gristDoc.appModel;
  private _storage = Deps.getIndexedDBStore();
  private _loadedSessions = Observable.create(this, new Map<string, AssistantSession>());
  private _loadPromise: Promise<void> | null = null;

  /** Set once a session is chosen, so that loading doesn't switch to the most recent one. */
  private _hasChosenSession = false;

  constructor(private _gristDoc: GristDoc) {
    super();

    this.activeSession = Computed.create(this, (use) => {
      const id = use(this.activeSessionId);
      return use(this.sessions).find(t => t.id === id) ?? null;
    });
    this.isActiveSessionLoading = Computed.create(this, (use) => {
      return Boolean(use(this.activeSession)) && !use(this._loadedSessions).has(use(this.activeSessionId));
    });
  }

  /**
   * Loads session summaries, and opens the most recent session unless one was already chosen. Only
   * loads once; later calls return the same promise.
   */
  public load(): Promise<void> {
    this._loadPromise ??= this._load();
    return this._loadPromise;
  }

  public newSession() {
    this.switchSession(uuidv4());
  }

  public switchSession(id: string) {
    this._hasChosenSession = true;
    this._openSession(id).catch(Deps.reportError);
  }

  /**
   * Returns the history of session `id`, or an empty history if it's new or not loaded yet.
   */
  public getHistory(id: string): ChatHistory {
    const session = this._loadedSessions.get().get(id);
    return {
      messages: session?.messages ?? [],
      conversationId: id,
      state: session?.state,
      developerPromptVersion: session?.developerPromptVersion ?? "default",
    };
  }

  /**
   * Saves the history of session `id`. New sessions are only stored once they have messages.
   */
  public saveHistory(id: string, history: ChatHistory) {
    const existing = this._loadedSessions.get().get(id);
    if (!existing && history.messages.length === 0) { return; }

    const now = Date.now();
    const session: AssistantSession = {
      id,
      title: existing?.title ?? titleFromMessages(history.messages),
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      messages: history.messages,
      state: history.state,
      developerPromptVersion: history.developerPromptVersion,
    };
    this._setLoaded(session);
    this._setSummary(toSummary(session));
    this._save(prefix => this._putSession(prefix, session));
  }

  /**
   * Renames session `id`. Does nothing if the trimmed title is empty.
   */
  public renameSession(id: string, title: string) {
    title = title.trim().slice(0, MAX_SESSION_TITLE_LENGTH);
    const summary = this.sessions.get().find(t => t.id === id);
    if (!title || !summary) { return; }

    const loaded = this._loadedSessions.get().get(id);
    if (loaded) {
      const renamed = { ...loaded, title };
      this._setLoaded(renamed);
      this._setSummary(toSummary(renamed));
      this._save(prefix => this._putSession(prefix, renamed));
    } else {
      // Contents aren't loaded, so only update the summary.
      const renamed = { ...summary, title };
      this._setSummary(renamed);
      this._save(prefix => this._storage.setItem(summaryKey(prefix, id), renamed));
    }
  }

  /**
   * Deletes session `id`. If it's active, switches to the most recent remaining session, or to a
   * new session if there are none.
   */
  public deleteSession(id: string) {
    this.sessions.set(this.sessions.get().filter(t => t.id !== id));
    const loaded = new Map(this._loadedSessions.get());
    loaded.delete(id);
    this._loadedSessions.set(loaded);
    this._save(prefix => Promise.all([
      this._storage.removeItem(summaryKey(prefix, id)),
      this._storage.removeItem(contentsKey(prefix, id)),
    ]));
    if (this.activeSessionId.get() === id) {
      this.switchSession(this.sessions.get()[0]?.id ?? uuidv4());
    }
  }

  // Sessions are stored against the trunk document, so that they're shared across its forks.
  private _keyPrefix() {
    const userId = this._appModel.currentUser?.id ?? 0;
    return `u:${userId};d:${parseUrlId(this._gristDoc.docId()).trunkId};`;
  }

  private async _putSession(prefix: string, session: AssistantSession) {
    const { messages, state, developerPromptVersion } = session;
    await this._storage.setItem(contentsKey(prefix, session.id), { messages, state, developerPromptVersion });
    await this._storage.setItem(summaryKey(prefix, session.id), toSummary(session));
  }

  private _hasSession(id: string) {
    return this.sessions.get().some(t => t.id === id);
  }

  private async _load(): Promise<void> {
    const [sessions, isPersistent] = await Promise.all([this._fetchSessions(), this._storage.isPersistent()]);
    if (this.isDisposed()) { return; }

    this.isPersistent.set(isPersistent);

    this.sessions.set(sortSessions([
      ...this.sessions.get(),
      ...sessions.filter(t => !this._hasSession(t.id)),
    ]));
    const mostRecent = this.sessions.get()[0];
    if (!this._hasChosenSession && mostRecent) {
      await this._openSession(mostRecent.id);
    }
    if (!this.isDisposed()) { this.isLoaded.set(true); }
  }

  private async _fetchSessions(): Promise<AssistantSessionSummary[]> {
    let sessions: AssistantSessionSummary[];
    try {
      sessions = await this._storage.getItems<AssistantSessionSummary>(summaryKey(this._keyPrefix(), ""));
    } catch (err) {
      log.warn("AssistantSessionStore: failed to load sessions", err);
      return [];
    }
    if (this.isDisposed()) { return []; }

    return await this._migrateLegacySession(sessions);
  }

  /**
   * Moves the single conversation that older versions kept in localStorage into a session.
   *
   * The localStorage copy is only removed if sessions are persisted, so that it isn't lost when
   * IndexedDB isn't available.
   */
  private async _migrateLegacySession(sessions: AssistantSessionSummary[]): Promise<AssistantSessionSummary[]> {
    const legacyStorage = Deps.getStorage();
    const legacyKey = `u:${this._appModel.currentUser?.id ?? 0};d:${this._gristDoc.docId()};assistantHistory`;
    const legacy = legacySession(legacyStorage.getItem(legacyKey));
    if (!legacy) { return sessions; }

    if (!sessions.some(t => t.id === legacy.id)) {
      try {
        await this._putSession(this._keyPrefix(), legacy);
      } catch (err) {
        // Keep the localStorage copy. Migration will be retried on next load.
        log.warn("AssistantSessionStore: failed to migrate legacy session", err);
        return sessions;
      }
      this._setLoaded(legacy);
      sessions = [toSummary(legacy), ...sessions];
    }
    if (await this._storage.isPersistent()) {
      legacyStorage.removeItem(legacyKey);
    }
    return sessions;
  }

  /**
   * Makes session `id` active, and loads its contents if it's stored and not loaded yet.
   *
   * If its contents are missing, deletes the session. If they fail to load, starts a new session
   * instead, so that what the user sends next doesn't overwrite the stored one.
   */
  private async _openSession(id: string) {
    this.activeSessionId.set(id);
    if (!this._hasSession(id) || this._loadedSessions.get().has(id)) { return; }

    let contents: AssistantSessionContents | undefined;
    try {
      contents = await this._storage.getItem<AssistantSessionContents>(contentsKey(this._keyPrefix(), id));
    } catch (err) {
      if (this.isDisposed()) { return; }

      Deps.reportError(err);
      if (this.activeSessionId.get() === id) { this.newSession(); }
      return;
    }
    if (this.isDisposed()) { return; }

    // Skip if the session was deleted, or loaded by another call, while this one was waiting.
    const summary = this.sessions.get().find(t => t.id === id);
    if (!summary || this._loadedSessions.get().has(id)) { return; }

    if (!contents) {
      log.warn("AssistantSessionStore: dropping session with missing contents", id);
      this.deleteSession(id);
      return;
    }

    this._setLoaded({ ...summary, ...contents });
  }

  private _save(save: (keyPrefix: string) => Promise<unknown>) {
    save(this._keyPrefix()).catch(err => log.warn("AssistantSessionStore: failed to save session", err));
  }

  private _setLoaded(session: AssistantSession) {
    this._loadedSessions.set(new Map(this._loadedSessions.get()).set(session.id, session));
  }

  private _setSummary(summary: AssistantSessionSummary) {
    this.sessions.set(sortSessions([summary, ...this.sessions.get().filter(t => t.id !== summary.id)]));
  }
}

function summaryKey(prefix: string, id: string) {
  return `${prefix}assistantSessionSummary:${id}`;
}

function contentsKey(prefix: string, id: string) {
  return `${prefix}assistantSession:${id}`;
}

/**
 * Returns a session from a JSON-encoded {@link ChatHistory}, or null if it has no messages.
 */
function legacySession(raw: string | null): AssistantSession | null {
  const legacy: ChatHistory | null = safeJsonParse(raw ?? "", null);
  if (!legacy || !Array.isArray(legacy.messages) || legacy.messages.length === 0) {
    return null;
  }

  const now = Date.now();
  return {
    id: legacy.conversationId || uuidv4(),
    title: titleFromMessages(legacy.messages),
    createdAt: now,
    updatedAt: now,
    messages: legacy.messages,
    state: legacy.state,
    developerPromptVersion: legacy.developerPromptVersion,
  };
}

function toSummary({ id, title, createdAt, updatedAt }: AssistantSession): AssistantSessionSummary {
  return { id, title, createdAt, updatedAt };
}

function sortSessions(sessions: AssistantSessionSummary[]): AssistantSessionSummary[] {
  return [...sessions].sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * Returns a session title from its first user message, truncated to
 * {@link MAX_SESSION_TITLE_LENGTH} characters.
 *
 * Titles are shown in the assistant panel's header and in the session history menu.
 */
function titleFromMessages(messages: ChatMessage[]): string {
  const firstUserMessage = messages.find(m => m.sender === "user");
  if (!firstUserMessage) { return t("Untitled session"); }

  const title = firstUserMessage.message.trim().replace(/\s+/g, " ");
  return title.length > MAX_SESSION_TITLE_LENGTH ?
    `${title.slice(0, MAX_SESSION_TITLE_LENGTH - 1)}…` :
    title || t("Untitled session");
}
